import React from "react";

/**
 * Top-level error boundary. Catches uncaught render errors anywhere below it
 * so the user gets a recoverable "Reload" screen instead of a white root div.
 *
 * Stays deliberately context-free — must be able to render even if the theme
 * or auth providers are the thing that crashed. Inline styles only.
 */

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary] Render error:", error, info.componentStack);
  }

  private handleReload = () => {
    globalThis.location.reload();
  };

  private handleHardReset = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      /* storage may be unavailable in private mode */
    }
    globalThis.location.replace(globalThis.location.pathname);
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV === "development";
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#fdf6f6",
          color: "#1a1a1a",
          fontFamily: "system-ui, -apple-system, sans-serif",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "3rem", marginBottom: 12 }}>😵</div>
        <h1 style={{ fontSize: "1.4rem", margin: "0 0 8px", fontWeight: 700 }}>
          Something went wrong
        </h1>
        <p style={{ fontSize: "0.9rem", margin: "0 0 20px", maxWidth: 360, opacity: 0.75 }}>
          OnyxLife hit an unexpected error. Reloading usually fixes it. If it keeps happening, try clearing local data.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            padding: "10px 22px",
            borderRadius: 999,
            border: "none",
            background: "#d63a5e",
            color: "#fff",
            fontSize: "0.95rem",
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "none",
            marginBottom: 10,
          }}
        >
          Reload
        </button>
        <button
          type="button"
          onClick={this.handleHardReset}
          style={{
            padding: "8px 16px",
            borderRadius: 999,
            border: "1px solid #d63a5e55",
            background: "transparent",
            color: "#d63a5e",
            fontSize: "0.78rem",
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "none",
          }}
        >
          Reload &amp; clear local data
        </button>
        {isDev && this.state.error && (
          <pre
            style={{
              marginTop: 24,
              padding: 12,
              maxWidth: "100%",
              overflow: "auto",
              fontSize: "0.7rem",
              color: "#a33",
              background: "#fff0f0",
              borderRadius: 8,
              textAlign: "left",
            }}
          >
            {this.state.error.message}
            {"\n\n"}
            {this.state.error.stack}
          </pre>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
